import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { BarChart } from '../../components/charts/BarChart.jsx';
import { ChartCard } from '../../components/charts/ChartCard.jsx';
import { DonutChart } from '../../components/charts/DonutChart.jsx';
import { LineChart } from '../../components/charts/LineChart.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { MetricCard } from '../../components/ui/Card.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';
import {
  toMovementsTrendSeries,
  toPoStatusSeries,
  toStockValueSeries,
  toTopProductsSeries,
} from './inventoryCharts.js';

const MONTH_OPTIONS = [6, 12, 24];
const OPEN_PO_STATUSES = new Set(['DRAFT', 'CONFIRMED', 'PARTIAL']);
const sum = (arr) => arr.reduce((a, b) => a + b, 0);

export function InventoryDashboardPage() {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const [months, setMonths] = useState(12);

  const stockValue = useQuery({ queryKey: ['inventory', 'stock-value'], queryFn: () => inventoryApi.stockValueByCategory().then((r) => r.items) });
  const poStatus = useQuery({ queryKey: ['inventory', 'po-status'], queryFn: () => inventoryApi.poStatus().then((r) => r.items) });
  const movements = useQuery({ queryKey: ['inventory', 'movements-trend', months], queryFn: () => inventoryApi.movementsTrend({ months }).then((r) => r.items) });
  const topProducts = useQuery({ queryKey: ['inventory', 'top-products'], queryFn: () => inventoryApi.topProducts().then((r) => r.items) });

  const stockSeries = toStockValueSeries(stockValue.data?.categories);
  const poSeries = toPoStatusSeries(poStatus.data?.statuses);
  const movementSeries = toMovementsTrendSeries(movements.data?.months);
  const topSeries = toTopProductsSeries(topProducts.data?.products);

  const totalStockValue = sum(stockSeries.values);
  const categoriesCount = stockSeries.labels.length;
  const openPo = (poStatus.data?.statuses || []).filter((s) => OPEN_PO_STATUSES.has(s.status));
  const openPoAmount = openPo.reduce((a, s) => a + (Number(s.amount) || 0), 0);
  const openPoCount = openPo.reduce((a, s) => a + (Number(s.count) || 0), 0);

  const accent = palette?.accent || '#1d4ed8';
  const outbound = palette?.series?.[4] || '#dc2626';

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('inventory.dashboard.title')}</h1>
          <p className="muted">{t('inventory.dashboard.subtitle')}</p>
        </div>
        <div className="period-switch">
          {MONTH_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              className={m === months ? 'period-btn active' : 'period-btn'}
              onClick={() => setMonths(m)}
            >
              {t('inventory.dashboard.lastMonths', { count: m })}
            </button>
          ))}
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('inventory.metric.stockValue')} value={formatCurrency(totalStockValue)} />
        <MetricCard label={t('inventory.metric.openPo')} value={formatCurrency(openPoAmount)} />
        <MetricCard label={t('inventory.metric.openPoCount')} value={formatNumber(openPoCount, 0)} />
        <MetricCard label={t('inventory.metric.categories')} value={formatNumber(categoriesCount, 0)} trendVariant="success" />
      </div>

      <div className="dashboard-grid">
        <ChartCard className="span-2" title={t('inventory.chart.movements')} subtitle={t('inventory.chart.movementsSub')}>
          {movements.isLoading ? <Spinner /> : movementSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <LineChart
              labels={movementSeries.labels}
              datasets={[
                { label: t('inventory.chart.inbound'), data: movementSeries.inbound, color: accent, fill: true },
                { label: t('inventory.chart.outbound'), data: movementSeries.outbound, color: outbound },
              ]}
            />
          )}
        </ChartCard>

        <ChartCard title={t('inventory.chart.stockValue')}>
          {stockValue.isLoading ? <Spinner /> : stockSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <DonutChart labels={stockSeries.labels} values={stockSeries.values} />
          )}
        </ChartCard>

        <ChartCard title={t('inventory.chart.poStatus')}>
          {poStatus.isLoading ? <Spinner /> : poSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={poSeries.labels} datasets={[{ label: t('inventory.chart.poCount'), data: poSeries.counts, color: accent }]} />
          )}
        </ChartCard>

        <ChartCard className="span-2" title={t('inventory.chart.topProducts')}>
          {topProducts.isLoading ? <Spinner /> : topSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={topSeries.labels} datasets={[{ label: t('inventory.metric.stockValue'), data: topSeries.values, color: accent }]} />
          )}
        </ChartCard>
      </div>
    </section>
  );
}
